import React, { lazy, Suspense, useContext } from "react";
import { Switch, Route, Redirect } from "react-router-dom";
import { StoreContext } from "./components/ContextStore/StoreContextProvider";

import { useUxVersion } from "./components/ContextStore/UxVersionContext";
import ErrorBoundary from "./components/errorBoundary/ErrorBoundary";
import BrandingLoader from "./components/Brandingloader/BrandingLoader";

// Define both V1 and V2 versions at module level for stable caching
// V1 versions
const HomePageV1 = lazy(() => import("./components/HomePage/HomePage"));
const LandingPageV1 = lazy(() => import("./components/LandingHomePage/LandingPage/LandingPage"));
const TeamLevelsViewV1 = lazy(() => import("./components/TeamLevelsView/TeamLevelsView"));

// V2 versions
const HomePageV2 = lazy(() => import("./components_upgrade/HomePage/HomePage"));
const LandingPageV2 = lazy(() => import("./components_upgrade/LandingHomePage/LandingPage/LandingPage"));
const TeamLevelsViewV2 = lazy(() => import("./components_upgrade/TeamLevelsView/TeamLevelsView"));
const DistributorTeamLevelsViewV1 = lazy(() =>
  import("./components/DistributorTeamLevelsView/DistributorTeamLevelsView")
);
const SmTrackingV1 = lazy(() => import("./components/SmTracking/SmTracking"));
const SmTrackingV2 = lazy(() => import("./components_upgrade/SmTracking/SmTracking"));
const DistributorTeamLevelsViewV2 = lazy(() =>
  import("./components_upgrade/DistributorTeamLevelsView/DistributorTeamLevelsView")
);
const DisciplinaryTeamLevelsViewV1 = lazy(() =>
  import("./components/DisciplinaryTeamLevelsView/DisciplinaryTeamLevelsView")
);
const DisciplinaryTeamLevelsViewV2 = lazy(() =>
  import("./components_upgrade/DisciplinaryTeamLevelsView/DisciplinaryTeamLevelsView")
);
const LoginPageV1 = lazy(() => import("./components/login/LoginMainPage/Login"));
const LoginPageV2 = lazy(() =>
  import("./components_upgrade/login/LoginMainPage/Login")
);
const FirstLoginV1 = lazy(() =>
  import("./components/login/Firstlogin/Firsttime")
);
const FirstLoginV2 = lazy(() =>
  import("./components_upgrade/login/Firstlogin/Firsttime")
);
const ForgotPasswordPageV1 = lazy(() =>
  import("./components/ForgotPassword/forgotPassword")
);
const ForgotPasswordPageV2 = lazy(() =>
  import("./components_upgrade/ForgotPassword/forgotPassword")
);
const UserAccess = lazy(() =>
  import("./components/UserAccessManagement/UserAccess/UserAccess")
);
const OperationalReportLandingPage = lazy(() =>
  import("./components/OperationalReport/OperationalReports")
);
const ReportPage = lazy(() =>
  import("./components/OperationalReport/ReportPage/ReportPage")
);
const TaskManagment = lazy(() => import("./components/TaskManagment/index"));
const DeviceGeoCompliance = lazy(() =>
  import("./components/DeviceGeoCompliance/DeviceGeoCompliance")
);
const EstimatedBestPrice = lazy(() =>
  import("./components/EstimatedBestPrice/EstimatedBestPrice")
);
const OutletStatus = lazy(() =>
  import("./components/OutletStatus/OutletStatus")
);
const SalesmanDeviceId = lazy(() => import("./components/SalesmanDeviceId/SalesmanDeviceId"))
const OutletGeoCompliance = lazy(() => import('./components/OutletGeoCompliance/OutletGeoCompliance'))
const AdminConfiguration = lazy(() =>
  import("./components/LandingHomePage/AdminConfiguration/AdmiinConfiguration")
);
const MerchandizingSets = lazy(() => import("./components/MerchandisingSets/MerchandizingSets"))
const MasterManagement = lazy(() => import("./components/MasterManagement/MasterManagement"))
const LeaveManagement = lazy(() => import("./components/LeaveManagement/LeaveManagement"))
const TargetMaster = lazy(() => import("./components/TargetMaster/TargetMaster"))

const PerfectStoreVisibilityDataManagement = lazy(() =>
  import("./components_upgrade/PerfectStore/PerfectStoreVisibilityDataManagement/PerfectStoreVisibilityDataManagement/PerfectStoreVisibilityDataManagement")
)
const UcubeHierarchyManagement = lazy(() =>
  import("./components_upgrade/UcubeHierarchyManagement/UcubeHierarchyManagement")
)

const Sos = lazy(() => import("./components/MerchandisingSets/SOSConfiguration/SosConfigurationPage"))
const Posm = lazy(() => import("./components/MerchandisingSets/POSMConfiguration/PosmConfigurationPage"))
const SurveyManagement = lazy(() => import("./components/MerchandisingSets/SurveyConfiguration/SurveyConfigurationPage"))

const Routes = () => {
  const { opsReportAccess, adminAccess, ucubeAccess} = useContext(StoreContext);
  const { isUpgraded } = useUxVersion();

  // Select component versions based on UX version
  const HomePage = isUpgraded ? HomePageV2 : HomePageV1;
  const LandingPage = isUpgraded ? LandingPageV2 : LandingPageV1;
  const TeamLevelsView = isUpgraded ? TeamLevelsViewV2 : TeamLevelsViewV1;
  const DistributorTeamLevelsView = isUpgraded ? DistributorTeamLevelsViewV2 : DistributorTeamLevelsViewV1;
  const DisciplinaryTeamLevelsView = isUpgraded ? DisciplinaryTeamLevelsViewV2 : DisciplinaryTeamLevelsViewV1;
  const SmTracking = isUpgraded ? SmTrackingV2 : SmTrackingV1;
  const LoginPage = isUpgraded ? LoginPageV2 : LoginPageV1;
  const FirstLogin = isUpgraded ? FirstLoginV2 : FirstLoginV1;
  const ForgotPasswordPage = isUpgraded ? ForgotPasswordPageV2 : ForgotPasswordPageV1;

  return (
    <ErrorBoundary>
      <Suspense fallback={<BrandingLoader />}>
        <Switch key={`switch-${isUpgraded}`}>
          <Route exact path={"/login"} component={LoginPage} />
          <Route exact path={"/signup"} component={FirstLogin} />
          <Route exact path={"/homepage"} component={HomePage} />
          <Route exact path={"/"} component={LandingPage} />
          <Route exact path={"/landingscreen"} component={LandingPage} />
          <Route exact path={"/forgotpassword"} component={ForgotPasswordPage} />
          {adminAccess && (
            <Route exact path={"/taskmanagment"} component={TaskManagment} />
          )}
          {adminAccess && (
            <Route
              exact
              path={"/devicegeocompliance"}
              component={DeviceGeoCompliance}
            />
          )}
          {adminAccess && (
            <Route exact path={"/outletStatus"} component={OutletStatus} />
          )}
          {adminAccess && (
            <Route
              exact
              path={"/adminconfigurationpage"}
              component={AdminConfiguration}
            />
          )}
          {ucubeAccess && (
            <Route exact path={"/teamlevels"} component={TeamLevelsView} />
          )}
          {ucubeAccess && (
            <Route
              exact
              path={"/distributorteamlevels"}
              component={DistributorTeamLevelsView}
            />
          )}
          {ucubeAccess && (
            <Route
              exact
              path={"/disciplinaryteamlevels"}
              component={DisciplinaryTeamLevelsView}
            />
          )}
          {adminAccess && (
            <Route exact path={"/useraccess"} component={UserAccess} />
          )}
          {adminAccess && (
            <Route
              exact
              path={"/estimatedbestprice"}
              component={EstimatedBestPrice}
            />
          )}
          {opsReportAccess && (
            <Route exact path={"/reportpage"} component={ReportPage} />
          )}
          {opsReportAccess && (
            <Route
              exact
              path={"/operationalreportslandingpage"}
              component={OperationalReportLandingPage}
            />
          )}
          {ucubeAccess && (
            <Route exact path={"/locate"} component={SmTracking} />
          )}
          {ucubeAccess && (
            <Route exact path={"/ucubehierarchymanagement"} component={UcubeHierarchyManagement} />
          )}

          {adminAccess && (
            <Route exact path={"/salesmandeviceid"} component={SalesmanDeviceId} />
          )}
          {adminAccess && (
            <Route exact path={"/outletGeoCompliance"} component={OutletGeoCompliance}/>
          )}
          {adminAccess && (
            <Route exact path={"/merchandizingSets"} component={MerchandizingSets} />
        )}
        {adminAccess && (
            <Route exact path={"/masterManagement"} component={MasterManagement} />
          )}
          {adminAccess && (
            <Route exact path={"/leaveManagement"} component={LeaveManagement} />
          )}
          {adminAccess && (
            <Route exact path={"/targetMaster"} component={TargetMaster} />
          )}
          {adminAccess && (
            <Route
              exact
              path={"/perfectstorevisibilitymanagement"}
              component={PerfectStoreVisibilityDataManagement}
            />
          )}
          {adminAccess && (
            <Route exact path={"/sosConfiguration"} component={Sos} />
          )}
          {adminAccess && (
            <Route exact path={"/posmConfiguration"} component={Posm} />
          )}
          {adminAccess && (
            <Route exact path={"/surveyConfiguration"} component={SurveyManagement} />
          )}

        {/* <Route exact path={'/outlets'} component={SchedulerPage} /> */}
        <Route path={"/*"} render={() => <Redirect to="/login" />} />
      </Switch>
    </Suspense>
    </ErrorBoundary>
  );
};

export default Routes;
